import { generatePath } from "react-router-dom";
import { companyTokenString } from "Utils/companyToken";
import { partnerURLSegment } from "Utils/partnerURLSegment";

import { appendAuthParams } from "Common/TokenAuth/Utils";

type FlowOverride = boolean | undefined;
type PartnerOverride = string | undefined;
type IDToken = string | null | undefined;
type RouteParams = Record<string, string | number | boolean>;
type RouteSearchParams = Record<string, unknown>;

interface URLBuilderOptions<Params, SearchParams> {
  /** Relative if React Aware, Absolute if not */
  relative: boolean;
  /** Can force renewal, partner, idToken, otherwise inherited from current URL */
  renewal: FlowOverride;
  partner: PartnerOverride;
  idToken: IDToken;
  authSearchParam: boolean;
  params: Params;
  searchParams: SearchParams;
}

const withPartnerAndCompanyPrefix = (path: string, idToken?: IDToken, partner?: PartnerOverride): string => {
  const partnerSegment = partner ?? partnerURLSegment();
  return `${partnerSegment}${companyTokenString(idToken)}${path}`;
};

export const baseName = "/small-group";
export const renewalBaseName = "/employer-renewal";

export const redirectName = "/small-group/employer-dashboard";

export const isAQuotePage = () => window.location.pathname.indexOf(baseName) !== -1;

export function isRenewal(): boolean {
  return !!window.location.pathname.match(/employer-renewal/);
}

const resolvedRenewal = (forceRenewal: FlowOverride): boolean => (forceRenewal === undefined ? isRenewal() : !!forceRenewal);

export function flowName(forceRenewal: FlowOverride): string {
  return resolvedRenewal(forceRenewal) ? renewalBaseName : baseName;
}

// Build a function that returns URLs. All parameters for our routing logic are optionally overridable.
// Also fills in dynamic segments if provided in params
// React Aware routing needs to skip partner and company token prefixes, because they are handled by the nested route configuration.
// Absolute routing needs to keep partner/company token prefixes.
const URLBuilder =
  <Params extends RouteParams = RouteParams, SearchParams extends RouteSearchParams = RouteSearchParams>(name: string) =>
  ({
    relative = true, // Relative if React Aware, Absolute if not
    renewal = undefined,
    partner = undefined,
    idToken = undefined,
    authSearchParam = true,
    params = {} as Params, // Used to fill in dynamic segments, e.g. /account/:idToken
    searchParams = {} as SearchParams,
  }: Partial<URLBuilderOptions<Params, SearchParams>> = {}) => {
    const partnerSegment = partner || partnerURLSegment();
    const baseSegment = flowName(renewal);
    const prefixSegments = relative ? "" : `${partnerSegment}${companyTokenString(idToken)}`;
    let url = generatePath(`${prefixSegments}${baseSegment}${name}`, params);
    if (Object.keys(searchParams).length > 0) {
      const queryString = new URLSearchParams(searchParams as Record<string, string>);
      url = `${url}?${queryString}`;
    }
    if (authSearchParam) {
      url = appendAuthParams(url);
    }
    return url;
  };

export const quotePageName = (renewalFlag: FlowOverride) => `${flowName(renewalFlag)}/select-plan`;
export const shoppingCartName = (renewalFlag: FlowOverride) =>
  resolvedRenewal(renewalFlag) ? "/employer-renewal/coverage-options" : "/employer-application/coverage-options";
export const downloadDecisionPacketName = (renewalFlag: FlowOverride) => `${flowName(renewalFlag)}/download-decision-packet`;
export const quotePageCartName = (renewalFlag: FlowOverride) => `${flowName(renewalFlag)}/select-plan/cart`;
export const planDetailsURL = URLBuilder("/select-plan/plan-details/:planID");
export const planPackageURL = URLBuilder("/select-plan/plan-package/:planID");

export const quotePageURL = (idToken: IDToken, renewalFlag: FlowOverride): string => withPartnerAndCompanyPrefix(quotePageName(renewalFlag), idToken);

export const comparePageURL = URLBuilder("/compare-plans");

export const shoppingCartURL = (idToken: IDToken, renewalFlag: FlowOverride): string => withPartnerAndCompanyPrefix(shoppingCartName(renewalFlag), idToken);

export const quotePageCartURL = (idToken: IDToken): string => withPartnerAndCompanyPrefix(quotePageCartName(undefined), idToken);
export const quotePageCartUrl = quotePageCartURL;

export const priceBreakdownURL = URLBuilder("/select-plan/price-breakdown/:planID");

export const comparePlansPriceBreakdownURL = URLBuilder("/compare-plans/price-breakdown/:planID");
export const comaprePlansPriceBreakdownURL = comparePlansPriceBreakdownURL;

export const enterCompanyDetailsURL = URLBuilder("/enter-company-details");
export const changeZIPCodeURL = URLBuilder("/change-zip-code");
export const downloadDecisionPacketURL = URLBuilder("/download-decision-packet");
